import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const fmt = (a, c) => `${c === "INR" ? "₹" : "$"}${Number(a).toLocaleString()}`;

export default function Dashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => { (async () => {
    if (!user) return;
    try {
      const o = await api.get("/orders/mine"); setOrders(o.data);
      if (user.role === "professional") {
        const p = await api.get("/payouts/mine"); setPayouts(p.data);
      }
      if (user.role === "admin") {
        const s = await api.get("/admin/stats"); setStats(s.data);
      }
    } catch (e) { console.error(e); }
  })(); }, [user]);

  if (!user) return <div className="p-12 text-center text-slate-500">Please login.</div>;

  const earned = payouts.filter(p => p.status === "released").reduce((s, p) => s + p.amount, 0);
  const pending = payouts.filter(p => p.status === "scheduled").reduce((s, p) => s + p.amount, 0);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-bold">Dashboard · {user.role}</div>
          <h1 className="font-display text-4xl font-black text-slate-900 mt-1">Welcome, {user.name}</h1>
        </div>
        {user.role === "admin" && <Link to="/admin"><Button className="btn-primary" data-testid="goto-admin">Open Admin</Button></Link>}
      </div>

      {user.role === "admin" && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            ["Users", stats.users], ["Clients", stats.clients], ["Professionals", stats.professionals],
            ["Services", stats.services], ["Orders", stats.orders], ["Paid Orders", stats.paid_orders],
            ["Pending Refunds", stats.pending_refunds], ["Scheduled Payouts", stats.scheduled_payouts],
          ].map(([k,v],i)=>(
            <div key={i} className="border border-slate-200 rounded p-4 bg-white" data-testid={`stat-${k.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="text-xs uppercase tracking-widest text-slate-500">{k}</div>
              <div className="font-display text-3xl font-black text-slate-900">{v}</div>
            </div>
          ))}
        </div>
      )}

      {user.role === "professional" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="border border-slate-200 rounded p-5 bg-white">
            <div className="text-xs uppercase tracking-widest text-slate-500">Earned</div>
            <div className="font-display text-3xl font-black text-slate-900">{fmt(earned, "INR")}</div>
            <div className="text-[11px] text-slate-500 mt-1">After 15% platform fee</div>
          </div>
          <div className="border border-slate-200 rounded p-5 bg-white">
            <div className="text-xs uppercase tracking-widest text-slate-500">Pending Payouts</div>
            <div className="font-display text-3xl font-black text-blue-600">{fmt(pending, "INR")}</div>
          </div>
          <div className="border border-slate-200 rounded p-5 bg-white">
            <div className="text-xs uppercase tracking-widest text-slate-500">Active Orders</div>
            <div className="font-display text-3xl font-black text-slate-900">{orders.filter(o=>o.status==="in_progress").length}</div>
          </div>
          <div className="border border-slate-200 rounded p-5 bg-white">
            <div className="text-xs uppercase tracking-widest text-slate-500">Completed</div>
            <div className="font-display text-3xl font-black text-slate-900">{orders.filter(o=>o.status==="completed").length}</div>
          </div>
        </div>
      )}

      <div className="border border-slate-200 rounded bg-white">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">My orders</h3>
          {user.role === "client" && <Link to="/services"><Button size="sm" data-testid="dash-browse-services">Browse services</Button></Link>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
              <tr><th className="p-3">Order</th><th>Service</th><th>Amount</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.order_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`order-row-${o.order_id}`}>
                  <td className="p-3 font-mono text-xs">{o.order_id}</td>
                  <td>{o.service_title}</td>
                  <td className="font-semibold">{fmt(o.amount, o.currency)}</td>
                  <td><Badge variant={o.status === "completed" ? "default" : "outline"}>{o.status}</Badge></td>
                  <td className="p-3"><Link to={`/orders/${o.order_id}`}><Button variant="outline" size="sm">Open</Button></Link></td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No orders yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
